import { useState, useEffect } from 'react';
import { api } from '../../lib/api';
import { Settings, Eye, EyeOff, Loader2, ChefHat, ToggleLeft, ToggleRight, Shield } from 'lucide-react';
import { toast } from 'sonner';

const SettingsChef = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await api.get('/settings/chef-access');
        setSettings(res.data);
      } catch { toast.error('Erreur chargement paramètres'); }
      setLoading(false);
    };
    fetch();
  }, []);

  const togglePage = (pageId) => {
    setSettings(prev => ({
      ...prev,
      pages: {
        ...prev.pages,
        [pageId]: { ...prev.pages[pageId], enabled: !prev.pages[pageId].enabled }
      }
    }));
  };

  const toggleFonc = (foncId) => {
    setSettings(prev => ({
      ...prev,
      fonctionnalites: {
        ...prev.fonctionnalites,
        [foncId]: { ...prev.fonctionnalites[foncId], enabled: !prev.fonctionnalites[foncId].enabled }
      }
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/settings/chef-access', settings);
      toast.success('Paramètres enregistrés');
    } catch { toast.error('Erreur sauvegarde'); }
    setSaving(false);
  };

  const enableAll = () => {
    setSettings(prev => ({
      pages: Object.fromEntries(Object.entries(prev.pages).map(([k, v]) => [k, { ...v, enabled: true }])),
      fonctionnalites: Object.fromEntries(Object.entries(prev.fonctionnalites).map(([k, v]) => [k, { ...v, enabled: true }])),
    }));
  };

  const disableAll = () => {
    setSettings(prev => ({
      pages: Object.fromEntries(Object.entries(prev.pages).map(([k, v]) => [k, { ...v, enabled: false }])),
      fonctionnalites: Object.fromEntries(Object.entries(prev.fonctionnalites).map(([k, v]) => [k, { ...v, enabled: false }])),
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="animate-spin text-lemas-gold" size={24} />
      </div>
    );
  }

  if (!settings) return null;

  const pagesEnabled = Object.values(settings.pages).filter(p => p.enabled).length;
  const pagesTotal = Object.keys(settings.pages).length;
  const foncsEnabled = Object.values(settings.fonctionnalites).filter(f => f.enabled).length;
  const foncsTotal = Object.keys(settings.fonctionnalites).length;

  return (
    <div className="space-y-6" data-testid="settings-chef-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-[rgb(var(--lm-text))] flex items-center gap-2">
            <Settings size={20} className="text-lemas-gold" /> Paramètres Chef
          </h2>
          <p className="text-xs text-[rgb(var(--lm-text-muted))] mt-1">
            Gérer les accès et fonctionnalités visibles par le Chef
          </p>
        </div>
        <button onClick={save} disabled={saving}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-lemas-gold text-[rgb(var(--lm-bg))] text-sm font-medium hover:bg-lemas-gold/90 transition-all disabled:opacity-50"
          data-testid="save-settings-btn">
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Shield size={14} />}
          Enregistrer
        </button>
      </div>

      {/* Summary */}
      <div className="lm-card p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ChefHat size={20} className="text-[rgb(var(--lm-green))]" />
          <div>
            <p className="text-sm text-[rgb(var(--lm-text))]">
              <span className="font-mono text-lemas-gold">{pagesEnabled}/{pagesTotal}</span> pages
              <span className="text-[rgb(var(--lm-text-muted))] mx-2">·</span>
              <span className="font-mono text-lemas-gold">{foncsEnabled}/{foncsTotal}</span> fonctionnalités
            </p>
            <p className="text-[10px] text-[rgb(var(--lm-text-muted))]">actives pour le Chef</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={enableAll} className="text-[10px] px-2 py-1 rounded bg-[rgb(var(--lm-green)/0.1)] text-[rgb(var(--lm-green))] hover:bg-[rgb(var(--lm-green)/0.2)] transition-colors"
            data-testid="enable-all-btn">
            Tout activer
          </button>
          <button onClick={disableAll} className="text-[10px] px-2 py-1 rounded bg-[rgb(var(--lm-red)/0.1)] text-[rgb(var(--lm-red))] hover:bg-[rgb(var(--lm-red)/0.2)] transition-colors"
            data-testid="disable-all-btn">
            Tout désactiver
          </button>
        </div>
      </div>

      {/* Pages section */}
      <div>
        <h3 className="text-[10px] tracking-[0.15em] uppercase text-[rgb(var(--lm-text-muted))] mb-3 flex items-center gap-1.5">
          <Eye size={10} /> Pages visibles
        </h3>
        <div className="lm-card divide-y divide-[rgb(var(--lm-border))]">
          {Object.entries(settings.pages).map(([id, page]) => (
            <div key={id} className="flex items-center justify-between px-4 py-3 hover:bg-[rgb(var(--lm-bg)/0.3)] transition-colors"
              data-testid={`page-toggle-${id}`}>
              <div>
                <p className={`text-sm font-medium ${page.enabled ? 'text-[rgb(var(--lm-text))]' : 'text-[rgb(var(--lm-text-muted))]'}`}>
                  {page.label}
                </p>
                <p className="text-[10px] text-[rgb(var(--lm-text-muted))]">{page.description}</p>
              </div>
              <button onClick={() => togglePage(id)} className="p-1 transition-all" data-testid={`toggle-page-${id}`}>
                {page.enabled ? (
                  <ToggleRight size={28} className="text-[rgb(var(--lm-green))]" />
                ) : (
                  <ToggleLeft size={28} className="text-[rgb(var(--lm-text-muted))]" />
                )}
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Features section */}
      <div>
        <h3 className="text-[10px] tracking-[0.15em] uppercase text-[rgb(var(--lm-text-muted))] mb-3 flex items-center gap-1.5">
          <Shield size={10} /> Fonctionnalités
        </h3>
        <div className="lm-card divide-y divide-[rgb(var(--lm-border))]">
          {Object.entries(settings.fonctionnalites).map(([id, fonc]) => (
            <div key={id} className="flex items-center justify-between px-4 py-3 hover:bg-[rgb(var(--lm-bg)/0.3)] transition-colors"
              data-testid={`fonc-toggle-${id}`}>
              <div>
                <p className={`text-sm font-medium ${fonc.enabled ? 'text-[rgb(var(--lm-text))]' : 'text-[rgb(var(--lm-text-muted))]'}`}>
                  {fonc.label}
                </p>
                <p className="text-[10px] text-[rgb(var(--lm-text-muted))]">{fonc.description}</p>
              </div>
              <button onClick={() => toggleFonc(id)} className="p-1 transition-all" data-testid={`toggle-fonc-${id}`}>
                {fonc.enabled ? (
                  <ToggleRight size={28} className="text-[rgb(var(--lm-green))]" />
                ) : (
                  <ToggleLeft size={28} className="text-[rgb(var(--lm-text-muted))]" />
                )}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SettingsChef;
